type QuillOp = {
  insert?: unknown;
  attributes?: Record<string, unknown>;
};

type QuillDelta = {
  ops?: QuillOp[];
};

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const applyInlineAttrs = (text: string, attrs: Record<string, unknown> = {}) => {
  let out = escapeHtml(text);
  if (!out) return '';

  if (attrs.bold) out = `<strong>${out}</strong>`;
  if (attrs.italic) out = `<em>${out}</em>`;
  if (attrs.underline) out = `<u>${out}</u>`;
  if (attrs.strike) out = `<s>${out}</s>`;
  if (attrs.link) {
    out = `<a href="${escapeHtml(attrs.link)}" target="_blank" rel="noopener noreferrer">${out}</a>`;
  }

  const styles: string[] = [];
  if (attrs.color) styles.push(`color:${attrs.color}`);
  if (attrs.background) styles.push(`background-color:${attrs.background}`);
  if (styles.length) out = `<span style="${styles.join(';')}">${out}</span>`;

  return out;
};

/** Convert Quill Delta ops to HTML for the HTML-based QuillEditor wrapper. */
export const quillOpsToHtml = (ops: QuillOp[]): string => {
  if (!Array.isArray(ops) || !ops.length) return '';

  let html = '';
  let line = '';
  let listType: string | null = null;

  const flushLine = (attrs: Record<string, unknown> = {}) => {
    const content = line || '&nbsp;';

    if (attrs.list) {
      if (listType !== attrs.list) {
        if (listType) html += listType === 'ordered' ? '</ol>' : '</ul>';
        html += attrs.list === 'ordered' ? '<ol>' : '<ul>';
        listType = String(attrs.list);
      }
      html += `<li>${content}</li>`;
    } else {
      if (listType) {
        html += listType === 'ordered' ? '</ol>' : '</ul>';
        listType = null;
      }
      html += `<p>${content}</p>`;
    }
    line = '';
  };

  ops.forEach((op) => {
    if (typeof op?.insert !== 'string') return;
    const attrs = op.attributes || {};
    const parts = op.insert.split('\n');

    parts.forEach((part, index) => {
      if (part) line += applyInlineAttrs(part, attrs);
      if (index < parts.length - 1) flushLine(attrs);
    });
  });

  if (line) flushLine({});
  if (listType) html += listType === 'ordered' ? '</ol>' : '</ul>';

  return html;
};

/** Parse stored blog content (JSON delta string or object) into a Quill delta. */
export const parseQuillOpsToDelta = (content: unknown): string | QuillDelta => {
  if (content == null || content === '') return '';

  if (typeof content === 'object') {
    if (Array.isArray(content)) return { ops: content as QuillOp[] };
    if (Array.isArray((content as QuillDelta).ops)) return content as QuillDelta;
    return '';
  }

  if (typeof content !== 'string') return String(content);

  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed)) return { ops: parsed as QuillOp[] };
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as QuillDelta).ops)) {
      return parsed as QuillDelta;
    }
  } catch {
    // plain text / legacy HTML
  }

  return content;
};

/** Hydrate Edit form state from API blog.content (Quill JSON or plain text). */
export const hydrateBlogEditorContent = (
  content: unknown
): { editorHtml: string; contentOpsJson: string } => {
  const parsed = parseQuillOpsToDelta(content);

  if (typeof parsed === 'string') {
    const trimmed = parsed.trim();
    if (!trimmed) return { editorHtml: '', contentOpsJson: '' };
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return { editorHtml: '', contentOpsJson: trimmed.startsWith('[') ? trimmed : '' };
    }
    return { editorHtml: parsed, contentOpsJson: '' };
  }

  const ops = parsed.ops || [];
  return {
    editorHtml: quillOpsToHtml(ops),
    contentOpsJson: ops.length ? JSON.stringify(ops) : '',
  };
};
